import { useState, useEffect, useRef } from 'react';
import { Storage } from '../storage';
import Modal from './Modal';

// ─── REPORT MODAL ─────────────────────────────────────────────────────────────
//
//  Generic modal for reporting any piece of content.
//  Props:
//    open         {boolean}   visibility
//    onClose      {function}  called when modal should close
//    contentType  {string}    'timeline' | 'comment' | 'reply' | 'user'
//    contentId    {string}    ID of the content being reported
//    onReported   {function}  called after a successful report (optional)

const REASONS = [
  { value: 'spam',          label: 'Spam',                  desc: 'Unsolicited or repetitive content' },
  { value: 'harassment',    label: 'Harassment',            desc: 'Targeting, bullying, or threatening someone' },
  { value: 'misinformation',label: 'Misinformation',        desc: 'False or misleading information' },
  { value: 'inappropriate', label: 'Inappropriate Content', desc: 'Explicit, offensive, or disturbing material' },
  { value: 'copyright',     label: 'Copyright Violation',   desc: 'Unauthorized use of copyrighted material' },
  { value: 'other',         label: 'Other',                 desc: 'Something else not listed here' },
];

const LABEL_MAP = {
  timeline: 'Timeline',
  comment:  'Comment',
  reply:    'Reply',
  user:     'User',
};

const MAX_DETAILS = 500;

const ReportModal = ({ open, onClose, contentType, contentId, onReported }) => {
  const [reason,    setReason]    = useState('');
  const [details,   setDetails]   = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState('');
  const [submitted, setSubmitted] = useState(false);
  const firstRadioRef = useRef(null);

  // Reset when opening.
  useEffect(() => {
    if (open) {
      setReason('');
      setDetails('');
      setAlsoBlock(false);
      setError('');
      setSubmitted(false);
      setLoading(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!reason) { setError('Please select a reason.'); return; }
    setLoading(true);
    setError('');
    try {
      await Storage.submitReport({ contentType, contentId, reason, details: details.trim() || undefined });
      if (alsoBlock && contentType === 'user') {
        await Storage.blockUser(contentId).catch(() => {});
      }
      setSubmitted(true);
      onReported?.();
    } catch (err) {
      if (err.message?.includes('already reported')) {
        setSubmitted(true);
      } else {
        setError(err.message || 'Failed to submit report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const label = LABEL_MAP[contentType] ?? 'Content';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={submitted ? 'Report Submitted' : `Report ${label}`}
      width={480}
    >
      {submitted ? (
        <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }} aria-hidden="true">✅</div>
          <p style={{ fontSize: 15, color: 'var(--text)', marginBottom: 8, fontWeight: 600 }}>
            Thank you for your report.
          </p>
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 28, lineHeight: 1.6 }}>
            Our moderation team will review it shortly.
          </p>
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      ) : (
        <div>
          {/* Reason radios */}
          <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px' }}>
            <legend style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 12, display: 'block' }}>
              Why are you reporting this? <span style={{ color: 'var(--danger)' }}>*</span>
            </legend>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {REASONS.map((r, i) => (
                <label
                  key={r.value}
                  className="report-reason-row"
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '10px 14px', borderRadius: 'var(--radius)',
                    border: `1px solid ${reason === r.value ? 'var(--accent)' : 'var(--border)'}`,
                    background: reason === r.value ? 'rgba(201,169,110,0.06)' : 'var(--bg3)',
                    cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => { setReason(r.value); setError(''); }}
                    ref={i === 0 ? firstRadioRef : null}
                    style={{ marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }}
                  />
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', display: 'block' }}>
                      {r.label}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text2)' }}>{r.desc}</span>
                  </div>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Optional details */}
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="report-details" style={{ fontSize: 13, color: 'var(--text2)', display: 'block', marginBottom: 8 }}>
              Additional context <span style={{ color: 'var(--text3)' }}>(optional)</span>
            </label>
            <textarea
              id="report-details"
              value={details}
              onChange={e => setDetails(e.target.value.slice(0, MAX_DETAILS))}
              placeholder="Provide any additional context that might help our team review this report"
              rows={3}
              style={{ width: '100%', resize: 'vertical', minHeight: 72, maxHeight: 160 }}
            />
            <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', marginTop: 4 }}>
              {details.length} / {MAX_DETAILS}
            </div>
          </div>

          {/* Also block — only for user reports */}
          {contentType === 'user' && (
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10,
              marginBottom: 20, cursor: 'pointer', fontSize: 13,
            }}>
              <input
                type="checkbox"
                checked={alsoBlock}
                onChange={e => setAlsoBlock(e.target.checked)}
                style={{ marginTop: 2, accentColor: 'var(--accent)', flexShrink: 0 }}
              />
              <div>
                <span style={{ fontWeight: 600, color: 'var(--text)', display: 'block', marginBottom: 2 }}>
                  Also block this user
                </span>
                <span style={{ color: 'var(--text3)', fontSize: 12, lineHeight: 1.5 }}>
                  They will not be notified. You can unblock them from your profile settings.
                </span>
              </div>
            </label>
          )}

          {/* Error */}
          {error && (
            <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 16 }}>
              {error}
            </p>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="btn-ghost btn-sm" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button className="btn-primary btn-sm" onClick={handleSubmit} disabled={loading || !reason}>
              {loading ? 'Submitting…' : 'Submit Report'}
            </button>
          </div>

          {/* Disclaimer */}
          <p style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5, textAlign: 'center' }}>
            Reports are reviewed by our moderation team. False reports may result in account action.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default ReportModal;
